import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ProjectAuditLogEntry, FIELD_LABELS } from '@/types/audit';
import { formatCurrency } from '../utils';
import { Clock, User, FileText, Plus, Edit, Trash2 } from 'lucide-react';

interface HistoryTabProps {
  projectId: string | undefined;
  getAuditLog: (projectId: string) => Promise<ProjectAuditLogEntry[]>;
}

export function HistoryTab({ projectId, getAuditLog }: HistoryTabProps) {
  const [entries, setEntries] = useState<ProjectAuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAuditLog() {
      if (!projectId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getAuditLog(projectId);
        setEntries(data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch audit log:', err);
        setError('Failed to load change history');
      } finally {
        setLoading(false);
      }
    }

    fetchAuditLog();
  }, [projectId, getAuditLog]);

  if (!projectId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Save the project first to view change history.</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Change History</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4 items-start">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-destructive">
          <p>{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (entries.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No changes recorded yet.</p>
          <p className="text-sm mt-2">
            Changes to this project will appear here after you save.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Change History
        </CardTitle>
      </CardHeader>
      <CardContent className="px-0 sm:px-6">
        {/* Desktop Table View */}
        <div className="hidden sm:block">
          <ScrollArea className="h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-40">Date</TableHead>
                  <TableHead className="w-32">By</TableHead>
                  <TableHead>Change</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-muted-foreground">
                      {formatDate(entry.changed_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="truncate max-w-24" title={entry.changed_by}>
                          {extractUsername(entry.changed_by)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ChangeDescription entry={entry} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>

        {/* Mobile Timeline View */}
        <div className="sm:hidden px-4">
          <div className="space-y-4">
            {entries.map((entry, index) => (
              <div
                key={entry.id}
                className={`relative pl-6 pb-4 ${
                  index !== entries.length - 1 ? 'border-l-2 border-muted' : ''
                }`}
              >
                <div className="absolute -left-1.5 top-0 h-3 w-3 rounded-full bg-primary" />
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{formatDate(entry.changed_at)}</span>
                    <span>•</span>
                    <span>{extractUsername(entry.changed_by)}</span>
                  </div>
                  <ChangeDescription entry={entry} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChangeDescription({ entry }: { entry: ProjectAuditLogEntry }) {
  const getChangeIcon = () => {
    switch (entry.change_type) {
      case 'create':
        return <Plus className="h-3.5 w-3.5 text-success" />;
      case 'update':
        return <Edit className="h-3.5 w-3.5 text-info" />;
      case 'delete':
        return <Trash2 className="h-3.5 w-3.5 text-destructive" />;
      default:
        return <Edit className="h-3.5 w-3.5" />;
    }
  };

  const getChangeTypeBadge = () => {
    switch (entry.change_type) {
      case 'create':
        return <Badge variant="default" className="bg-success/15 text-success">Created</Badge>;
      case 'update':
        return <Badge variant="default" className="bg-info/15 text-info">Updated</Badge>;
      case 'delete':
        return <Badge variant="default" className="bg-destructive/15 text-destructive">Deleted</Badge>;
      default:
        return null;
    }
  };

  if (entry.change_summary) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {getChangeTypeBadge()}
        <span className="text-sm">{entry.change_summary}</span>
      </div>
    );
  }

  const fieldLabel = entry.field_name ? FIELD_LABELS[entry.field_name] || entry.field_name : 'Project';

  if (entry.change_type === 'create') {
    return (
      <div className="flex items-center gap-2">
        {getChangeTypeBadge()}
        <span className="text-sm">Project created</span>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        {getChangeTypeBadge()}
        <span className="text-sm font-medium">{fieldLabel}</span>
      </div>
      <div className="text-sm text-muted-foreground">
        <span className="line-through">{formatValue(entry.old_value)}</span>
        <span className="mx-2">→</span>
        <span className="font-medium text-foreground">{formatValue(entry.new_value)}</span>
      </div>
    </div>
  );
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function extractUsername(email: string): string {
  return email.split('@')[0];
}

function formatValue(value: any): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') {
    // Check if it looks like currency (> 100 or has decimal)
    if (value > 100) return formatCurrency(value);
    if (value <= 100 && value > 0) return `${value}%`;
    return value.toString();
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return 'None';
    return value.map(v => typeof v === 'object' ? v.name || JSON.stringify(v) : v).join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}
